import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    if (standalone) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return; // 7 days
    }

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    if (isiOS) {
      // iOS doesn't have beforeinstallprompt, show manual instructions
      setTimeout(() => setShowBanner(true), 3000);
      return;
    }

    // Android/Chrome: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showBanner || isStandalone) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[90] p-3 animate-in slide-in-from-bottom" data-testid="pwa-install-banner">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg, #0a1b3d, #4578A6)' }}>
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm" style={{ color: '#0a1b3d' }}>Installer Pointage MAA</h3>
            {isIOS ? (
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                Appuyez sur <span className="inline-flex items-center bg-gray-100 px-1.5 py-0.5 rounded text-[10px] font-medium">
                  <svg className="w-3 h-3 mr-0.5" fill="currentColor" viewBox="0 0 20 20"><path d="M15 8a1 1 0 00-1-1h-3V4a1 1 0 00-2 0v3H6a1 1 0 000 2h3v3a1 1 0 002 0V9h3a1 1 0 001-1z"/><path fillRule="evenodd" d="M2 14a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2zm2 0h12v2H4v-2z" clipRule="evenodd"/></svg>
                  Partager
                </span> puis <strong>"Sur l'ecran d'accueil"</strong>
              </p>
            ) : (
              <p className="text-xs text-gray-500 mt-1">Ajoutez l'app sur votre ecran d'accueil pour un acces rapide</p>
            )}
          </div>
          <button onClick={handleDismiss} className="p-1 text-gray-400 hover:text-gray-600 shrink-0" data-testid="pwa-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstall}
            className="w-full mt-3 py-2.5 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #4578A6, #0a1b3d)' }}
            data-testid="pwa-install-btn"
          >
            <Download className="w-4 h-4" /> Installer l'application
          </button>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
